import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

interface ProgressRingProps {
	progress: number;
	size?: "sm" | "md" | "lg";
	showCompletedIcon?: boolean;
	className?: string;
}

const sizes = {
	sm: { dimension: 32, stroke: 3, fontSize: "text-[9px]", iconSize: 14 },
	md: { dimension: 48, stroke: 4, fontSize: "text-xs", iconSize: 18 },
	lg: { dimension: 64, stroke: 5, fontSize: "text-sm", iconSize: 24 },
};

export function ProgressRing({
	progress,
	size = "md",
	showCompletedIcon = false,
	className,
}: ProgressRingProps) {
	const { dimension, stroke, fontSize, iconSize } = sizes[size];
	const radius = (dimension - stroke) / 2;
	const circumference = 2 * Math.PI * radius;
	const percent = Math.min(100, Math.max(0, progress));
	const offset = circumference - (percent / 100) * circumference;

	const isCompleted = showCompletedIcon && percent >= 100;

	return (
		<div
			className={cn("relative inline-flex items-center justify-center", className)}
			style={{ width: dimension, height: dimension }}
		>
			<svg width={dimension} height={dimension} className="-rotate-90" aria-hidden="true">
				<circle
					cx={dimension / 2}
					cy={dimension / 2}
					r={radius}
					fill="none"
					stroke="currentColor"
					strokeWidth={stroke}
					className="text-bg-overlay"
				/>
				<circle
					cx={dimension / 2}
					cy={dimension / 2}
					r={radius}
					fill="none"
					stroke="currentColor"
					strokeWidth={stroke}
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					className={cn(
						"transition-[stroke-dashoffset] duration-500 ease-out motion-reduce:transition-none",
						isCompleted ? "text-success" : "text-accent",
					)}
				/>
			</svg>
			{isCompleted ? (
				<Check
					className="absolute text-success"
					style={{ width: iconSize, height: iconSize }}
					aria-hidden="true"
				/>
			) : (
				<span className={cn("absolute font-medium text-primary", fontSize)}>
					{Math.round(percent)}%
				</span>
			)}
		</div>
	);
}
